import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Download, Send, Loader2, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import PodPhotos from "../components/PodPhotos";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import {
  subscribeJob, subscribeStops, downloadAllPodPhotos, sendPodEmail, sendReportEmail, stopPhotos, STOP_STATUS_META
} from "../lib/deliveries";

export default function AdminManagePOD() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const [job, setJob] = useState(null);
  const [stops, setStops] = useState([]);
  const [zipping, setZipping] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [sendingStop, setSendingStop] = useState(null);

  useEffect(() => { const u1 = subscribeJob(jobId, setJob); const u2 = subscribeStops(jobId, setStops); return () => { u1(); u2(); }; }, [jobId]);

  if (!job) return <div className="container-518 py-20 text-center text-slate-500">Loading…</div>;

  const withPhotos = stops.filter((s) => stopPhotos(s).length).length;

  const downloadAll = async () => {
    setZipping(true);
    try { const n = await downloadAllPodPhotos(job, stops); toast({ title: "ZIP ready", description: `${n} POD photo(s) downloaded.` }); }
    catch (e) { toast({ title: "Download failed", description: e?.message }); }
    finally { setZipping(false); }
  };

  const sendReport = async () => {
    setSendingReport(true);
    try { await sendReportEmail({ job, stops }); toast({ title: "Delivery report sent", description: (job.notify_emails || []).join(", ") }); }
    catch (e) { toast({ title: "Couldn't send report", description: e?.message }); }
    finally { setSendingReport(false); }
  };

  const sendStop = async (stop) => {
    setSendingStop(stop.id);
    try { await sendPodEmail({ job, stop }); toast({ title: "POD sent", description: `${stop.package_reference} shared with client.` }); }
    catch (e) { toast({ title: "Couldn't send POD", description: e?.message }); }
    finally { setSendingStop(null); }
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 py-8">
          <Link to={`/admin/deliveries/${jobId}`} className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> Back to job</Link>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-1">Manage POD photos · {job.job_reference}</div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold">{job.job_name}</h1>
          <p className="text-slate-300 mt-1 text-sm">{job.client_name} · {stops.length} stops · {withPhotos} with photos</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Button onClick={downloadAll} disabled={zipping} data-testid="download-all-pod" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-10 px-4 font-semibold">
              {zipping ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />} Download all POD photos
            </Button>
            <Button onClick={sendReport} disabled={sendingReport} data-testid="send-report" className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-10 px-4 font-semibold">
              {sendingReport ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />} Send delivery report
            </Button>
          </div>
        </div>
      </section>

      <section className="container-518 py-8 max-w-4xl space-y-3" data-testid="manage-pod-list">
        {stops.map((s) => {
          const m = STOP_STATUS_META[s.status] || STOP_STATUS_META.pending;
          return (
            <div key={s.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`manage-pod-stop-${s.stop_number}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-extrabold text-slate-400">{String(s.stop_number).padStart(2, "0")}</span>
                    <span className="text-xs font-bold text-[#f59321]">{s.package_reference}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${m.cls}`}>{m.label}</span>
                  </div>
                  <div className="font-bold text-[#0f2847] text-sm mt-0.5 truncate">{s.recipient_name}{s.company_name ? ` · ${s.company_name}` : ""}</div>
                  <div className="text-xs text-slate-500 truncate">{s.address_line_1}, {s.city}, {s.state} {s.zip_code}</div>
                </div>
                <Button onClick={() => sendStop(s)} disabled={sendingStop === s.id || !stopPhotos(s).length} size="sm" variant="outline" className="rounded-full h-9 px-4 font-semibold border-2 shrink-0" data-testid={`send-pod-${s.stop_number}`}>
                  {sendingStop === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-3.5 h-3.5 mr-1.5" /> Send POD</>}
                </Button>
              </div>
              <PodPhotos job={job} stop={s} user={user} canEdit />
            </div>
          );
        })}
        {stops.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
            <Package className="w-10 h-10 text-slate-300 mx-auto mb-3" /> No stops found for this job.
          </div>
        )}
      </section>
    </div>
  );
}
